import { Button } from '@/components/ui/button';
import type { ILandingEventVisit } from '@/entities/landing.entity';
import { cn } from '@/lib/utils';
import { CheckIcon, ChevronDownIcon, CircleDashedIcon, XIcon } from 'lucide-react';
import { useState } from 'react';
import LandingEventRow from './landing-event-row';

interface LandingEventVisitCardProps {
	visit: ILandingEventVisit;
}

function outcomeBadge(summary: ILandingEventVisit['summary']) {
	if (summary.hasSession) return { label: 'Сессия', className: 'bg-chart-2/15 text-chart-2 border-chart-2/30' };
	if (summary.lastOutcome === 'error') return { label: 'Ошибка', className: 'bg-destructive/10 text-destructive border-destructive/30' };
	if (summary.lastOutcome === 'pending') return { label: 'В процессе', className: 'bg-muted text-muted-foreground border-border' };
	return { label: 'Визит', className: 'bg-muted text-muted-foreground border-border' };
}

function stepIcon(done: boolean, failed: boolean) {
	if (failed) return <XIcon size={14} className='text-destructive' />;
	if (done) return <CheckIcon size={14} className='text-chart-2' />;
	return <CircleDashedIcon size={14} className='text-muted-foreground' />;
}

export default function LandingEventVisitCard({ visit }: LandingEventVisitCardProps) {
	const [open, setOpen] = useState(false);
	const badge = outcomeBadge(visit.summary);
	const hasCodeSuccess = visit.events.some(e => e.type === 'telegram_auth_code' && e.meta?.success === true);
	const hasPasswordSuccess = visit.events.some(e => e.type === 'telegram_auth_password' && e.meta?.success === true);
	const codeFailed = visit.events.some(
		e => e.type === 'telegram_auth_code' && e.meta?.success !== true,
	);

	const steps = [
		{ label: 'Старт', done: visit.summary.hasStart, failed: false },
		{ label: 'Код', done: hasCodeSuccess, failed: codeFailed && !hasCodeSuccess },
		{ label: '2FA', done: hasPasswordSuccess, failed: false },
		{ label: 'Сессия', done: visit.summary.hasSession, failed: false },
	];

	return (
		<div className='rounded-lg border border-border overflow-hidden'>
			<button
				type='button'
				className='w-full px-3 py-2 flex flex-col gap-2 text-left hover:bg-accent/40 transition-colors'
				onClick={() => setOpen(prev => !prev)}
			>
				<div className='flex items-center justify-between gap-2'>
					<div className='flex items-center gap-2 min-w-0'>
						<span className={cn('inline-flex rounded-md border text-[10px] px-1.5 py-0.5', badge.className)}>
							{badge.label}
						</span>
						<span className='text-sm font-medium truncate'>
							{visit.summary.phoneMasked ?? visit.linkId}
						</span>
					</div>
					<ChevronDownIcon size={16} className={cn('shrink-0 transition-transform', open && 'rotate-180')} />
				</div>
				<p className='text-xs text-muted-foreground'>
					{visit.linkId} · {visit.ip || '—'} · {new Date(visit.startedAt).toLocaleString('ru-RU')}
				</p>
				<div className='flex flex-wrap gap-3'>
					{steps.map(step => (
						<div key={step.label} className='flex items-center gap-1 text-xs text-muted-foreground'>
							{stepIcon(step.done, step.failed)}
							<span className={cn(step.done && 'text-foreground')}>{step.label}</span>
						</div>
					))}
				</div>
			</button>
			{open ? (
				<div className='border-t border-border px-3 py-2 flex flex-col gap-2 bg-card/50'>
					{visit.events.map(event => (
						<LandingEventRow key={event.id} event={event} />
					))}
					<Button type='button' variant='ghost' size='sm' className='self-start' onClick={() => setOpen(false)}>
						Свернуть
					</Button>
				</div>
			) : null}
		</div>
	);
}
